import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetStorageUnits, dataSetSites, dataSetStorageLocations } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IStorageUnit } from '../../../@types/model/masterData/storageUnit/storageUnit';
import StorageUnitHttpService from '../../../services/http/masterData/storageUnitHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { IStorageUnitFormValues } from '../../../@types/model/masterData/storageUnit/storageUnitFormValues';
import StorageUnitForm from './form/StorageUnitForm';
import SiteHttpService from '../../../services/http/masterData/siteHttpService';
import { useLocation } from 'react-router';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import { setSiteMasterDataIndexedDB, setStorageLocationMasterDataIndexedDB, setStorageUnitMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import PillButton from '../../../components/input/PillButton';
import { IApiError } from '../../../@types/other';
import { generateStorageUnitCards, renderQRCodes } from '../../../services/http/masterData/storageUnitDocumentService';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import StorageUnitModelHelper from '../../../@types/model/masterData/storageUnit/storageUnitModelHelper';
import { CustomTable } from '../../../components/table/CustomTableWrapper';
import StorageLocationHttpService from '../../../services/http/masterData/storageLocationHttpService';

const StorageUnitScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const storageUnits = useAppSelector(state => state.masterData.storageUnits);
    const storageLocations = useAppSelector(state => state.masterData.storageLocations);
    const sites = useAppSelector(state => state.masterData.sites);
    const selectedSiteIds = useAppSelector(x => x.data.selectedSiteIds);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedStorageUnit, setSelectedStorageUnit] = useState<IStorageUnit | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingStorageUnit, setDeletingStorageUnit] = useState<IStorageUnit | null>(null);
    const [selectedStorageUnits, setSelectedStorageUnits] = useState<Array<number | string>>([]);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const [storageUnitRes, siteRes, storageLocationRes] = await Promise.all([
                    StorageUnitHttpService.getStorageUnitData(),
                    SiteHttpService.getSiteData(),
                    StorageLocationHttpService.getList(),
                ]);
                dispatch(dataSetSites(siteRes.data));
                dispatch(dataSetStorageUnits(storageUnitRes.data));
                dispatch(dataSetStorageLocations(storageLocationRes.data));
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading storage units.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const refreshData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            try {
                setIsLoading(true);
                const [storageUnitRes, siteRes, storageLocationRes] = await Promise.all([
                    StorageUnitHttpService.getStorageUnitData(),
                    SiteHttpService.getSiteData(),
                    StorageLocationHttpService.getList(),
                ]);
                dispatch(dataSetSites(siteRes.data));
                dispatch(dataSetStorageUnits(storageUnitRes.data));
                dispatch(dataSetStorageLocations(storageLocationRes.data));

                await setSiteMasterDataIndexedDB(siteRes.data);
                await setStorageUnitMasterDataIndexedDB(storageUnitRes.data);
                await setStorageLocationMasterDataIndexedDB(storageLocationRes.data);
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred loading storage units.');
            } finally {
                setIsLoading(false);
            }
        } else {
            fetchData();
        }
    };

    const onSubmit = async (values : IStorageUnitFormValues) => {
        setIsLoading(true);

        try {
            const upsert = StorageUnitModelHelper.createUpsert(values, selectedStorageUnit);
            const res = await StorageUnitHttpService.addOrUpdateStorageUnit(upsert);
            const updatedList = upsertArrayElement(storageUnits, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetStorageUnits(updatedList));
            await setStorageUnitMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedStorageUnit ? 'Storage unit updated successfully.' : 'Storage unit added successfully.');
            onModalDismiss();
            refreshData();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating storage unit data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeStorageUnit = async () => {
        if (!deletingStorageUnit) return;
        const updatedStorageUnit = { ...deletingStorageUnit, isActive: false };
        setIsLoading(true);
        try {
            const res = await StorageUnitHttpService.deleteStorageUnit(updatedStorageUnit.id);
            const updatedList = upsertArrayElement(storageUnits, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetStorageUnits(updatedList));
            await setStorageUnitMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Storage unit deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting storage unit.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    const onGenerateStorageUnitCards = useCallback(async () => {
        setIsLoading(true);
        try {
            await generateStorageUnitCards(storageUnits.filter(x => selectedStorageUnits.includes(x.id)));
            generalShowSuccessSnackbar('Storage unit cards generated successfully.');
            setSelectedStorageUnits([]);
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred generating storage unit cards.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedStorageUnits]);

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedStorageUnit(null);
    };

    const edit = (selectedRow : IStorageUnit) => {
        setSelectedStorageUnit(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IStorageUnit) : void => {
        setDeletingStorageUnit(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingStorageUnit(null);
    };

    const deleteButtonTooltip = (row : IStorageUnit) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Storage Unit', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const getSiteShortDescription = (siteId : number) => {
        const site = sites.find(x => x.id === siteId);
        return site?.shortDescription ?? '';
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            const defaultSite = selectedSiteIds?.length === 1
                ? sites.find(x => x.id === selectedSiteIds[0])
                : null;

            return StorageUnitModelHelper.createFormValues(
                selectedStorageUnit,
                defaultSite ?? null,
                sites,
                storageLocations
            );
        }
    }, [
        selectedStorageUnit,
        sites,
        isDialogOpen,
        selectedSiteIds,
        storageLocations
    ]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className={'fdc hfill'}>
                <div className={'fdr jcfs mb10'}>
                    <PillButton
                        text={'Generate Storage Unit Cards'}
                        onClick={onGenerateStorageUnitCards}
                        disabled={selectedStorageUnits.length === 0}
                        color={'secondary'}
                    />
                </div>
                <Card className={'fdc hfill'}>
                    <CustomTable<IStorageUnit>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Storage Unit', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Storage Unit', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IStorageUnit) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}

                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Site', field: 'siteId', formatFunction: getSiteShortDescription, enableFiltering: true, enableSorting: true },
                            { title: 'Latitude', field: 'latitude', enableFiltering: true, enableSorting: true },
                            { title: 'Longitude', field: 'longitude', enableFiltering: true, enableSorting: true },
                            { title: 'Is Tippable', field: 'isTippable', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Has Raw Stock', field: 'hasRawStock', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={storageUnits}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {/* renderQRCodes is needed for generating storage unit cards */}
            {renderQRCodes(storageUnits)}
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedStorageUnit ? 'Edit Storage Unit' : 'Add Storage Unit'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'lg'}
                    fullWidth
                >
                    <StorageUnitForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Delete Storage Unit'}
                open={!!deletingStorageUnit}
                description={'Are you sure you want to delete this storage unit?'}
                onAccept={removeStorageUnit}
                onClose={closeDeletePopup}
                dialogType={'orange'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default StorageUnitScreen;
